import { isAxiosError } from "axios";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthProvider";
import { useAnalytics } from "../contexts/analytics/hooks";
import { useSnackbar } from "../contexts/SnackbarContext";
import { backendService } from "../services/backend.service";
import { localStorageService } from "../services/LocalStorageService";
import { requestDepartmentToken } from "../utils/departmentToken";
import { uiVisibleErrorKey } from "../utils/errors";
import { UserView } from "../utils/models";
import { canSwitchBetweenViews, resolveDepartmentIdForView } from "../utils/utilFunctions";

/** The current view (admin/staff) and the ability to switch. Source of truth is userInfo, not the JWT. */
export interface ViewMode {
	/** Undefined while userInfo is loading, or for a pre-view token. Treat as "not known yet". */
	view: UserView | undefined;
	/** True if the user can actually reach both views — admin somewhere AND a nurse somewhere. */
	canSwitchView: boolean;
	/** True while a switch is in flight. */
	isSwitching: boolean;
	/** Move to the other view. Resolves only on failure — success reloads the page. */
	switchView: () => Promise<void>;
}

export const useViewMode = (): ViewMode => {
	const { userInfo, saveTokenData } = useAuth();
	const { track, trackError } = useAnalytics();
	const { showSnackbar } = useSnackbar();
	const { t } = useTranslation();
	const [isSwitching, setIsSwitching] = useState(false);

	const view = userInfo.view;
	const canSwitchView = canSwitchBetweenViews(userInfo.adminDepartmentIds, userInfo.staffDepartmentIds);
	// Re-impersonating needs the target's login id AND the support token that authorized it —
	// the ambient token belongs to the impersonated user, who cannot impersonate anyone. By id, not
	// username or phone: phone-only logins have no username, and phones are shared between logins.
	const impersonatedUserId = userInfo.impersonated_by ? userInfo.id : undefined;
	const supportToken = localStorageService.getOriginalToken() ?? undefined;

	// The department the user is standing in now: the stored choice if still valid, else the first.
	const currentDepartmentId = useMemo(() => {
		const departmentIds = userInfo.departments?.map((department) => department.department_id) ?? [];
		const storedDepartmentId = localStorageService.getChosenDepartmentID();
		return storedDepartmentId && departmentIds.includes(storedDepartmentId) ? storedDepartmentId : departmentIds[0];
	}, [userInfo.departments]);

	const resolveTargetDepartmentId = useCallback(
		(targetView: UserView): string | undefined =>
			resolveDepartmentIdForView(targetView, currentDepartmentId, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds),
		[currentDepartmentId, userInfo.adminDepartmentIds, userInfo.staffDepartmentIds],
	);

	const switchView = useCallback(async () => {
		// view is undefined only before userInfo has loaded.
		if (isSwitching || !view) return;

		const targetView = view === UserView.admin ? UserView.staff : UserView.admin;
		// True once the reload is under way, so the button stays disabled until the page goes.
		let reloading = false;
		setIsSwitching(true);
		track("view_switch_start", { from: view, to: targetView });

		try {
			const departmentId = resolveTargetDepartmentId(targetView);
			if (!departmentId) {
				// Only reachable if the button rendered on stale data.
				track("view_switch_error", { from: view, to: targetView, reason: "no_target_department" });
				showSnackbar(t("error.view.switch.failed"), "error");
				return;
			}

			// While impersonating, /token/department is wrong: it authenticates off the refresh token,
			// which belongs to the SUPPORT user, so it would end the impersonation. Re-impersonate
			// into the target view instead.
			const targetDepartmentId = departmentId;
			// Without the support token the department exchange below would quietly end the impersonation.
			if (impersonatedUserId && !supportToken) throw new Error("impersonating without the support token to switch with");
			const token =
				impersonatedUserId && supportToken
					? (await backendService.impersonateUserInView(impersonatedUserId, targetDepartmentId, targetView, supportToken)).data
					: await requestDepartmentToken(targetDepartmentId, targetView);

			if (impersonatedUserId && supportToken) {
				// Impersonation always returns a token; an MFA-shaped response here is a server bug.
				const impersonationToken = token.access_token;
				if (!impersonationToken) throw new Error("impersonation response carried no access token");
				// Saved before the reload, so nothing the reload cuts off (on the phone, every request in flight)
				// can remove it. The restarted app reopens the impersonation from it.
				localStorageService.setImpersonationToken(impersonationToken);
			}

			// The reload drops the in-memory token and start-up rebuilds the session from storage, so save both.
			localStorageService.setChosenDepartmentID(targetDepartmentId);
			localStorageService.setChosenView(targetView);
			track("view_switch_success", { from: view, to: targetView, department_id: targetDepartmentId });

			// Reload before installing a department token, or the new role is checked against the old URL and flashes /404.
			window.location.replace(`/?ts=${Date.now()}`);
			// The page is on its way out — don't let `finally` re-enable the menu behind it.
			reloading = true;

			if (!(impersonatedUserId && supportToken)) await saveTokenData(token);
		} catch (error) {
			trackError("view_switch_error", {
				from: view,
				to: targetView,
				reason: "request_failed",
				error_message: error instanceof Error ? error.message : String(error),
				status: isAxiosError(error) ? error.response?.status : undefined,
			});
			// The backend names which view is not open here (admin or staff), so show its reason.
			// DEFAULT: any other failure — network, or a refusal without a reason — gets the generic message.
			showSnackbar(t(uiVisibleErrorKey(error) ?? "error.view.switch.failed"), "error");
		} finally {
			if (!reloading) setIsSwitching(false);
		}
	}, [isSwitching, view, impersonatedUserId, supportToken, resolveTargetDepartmentId, saveTokenData, showSnackbar, t, track, trackError]);

	return { view, canSwitchView, isSwitching, switchView };
};
